import { Component, Input, AfterViewInit, OnDestroy, OnInit, DoCheck } from '@angular/core';
import { NewEnvelopeService } from '../new-envelope/new-envelope.service';
import { NewEnvelopeSteps } from 'src/app/enum/new-envelope-steps';
import { IToast, ToastService } from 'codex-lib';
import { TranslateService } from '@ngx-translate/core';
import { ComponentDragDropService } from '../services/component-drag-drop.service';
import { Signatory } from '../model/signatory';
import { InteractService } from '../services/interact.service';
import { BrowserUtil } from 'src/app/util/browser-util';
import { Subscription } from 'rxjs';
import { TrackerData } from 'src/app/modules/google-analytics/model/tracker-data.model';
import { AnalyticEventClient } from 'src/app/enum/analytic-event-client.enum';
import { AnalyticsService } from 'src/app/modules/google-analytics/service/analytics.service';
import interact from 'interactjs';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';

@Component({
  selector: 'app-envelope-sidebar-custom-fields',
  templateUrl: './envelope-sidebar-custom-fields.component.html',
  styleUrls: ['./envelope-sidebar-custom-fields.component.scss']
})
export class EnvelopeSidebarCustomFieldsComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
  CustomFieldTypeEnum = CustomFieldTypeEnum;

  @Input() set signatories(value: any[]) {
    this._signatories = value;
    this.resetDropdown();
  }

  NewEnvelopeSteps = NewEnvelopeSteps;
  signatorySelected: any;
  classComponent: string = 'drag-drop disabled';
  isMobile: boolean = BrowserUtil.isMobile();
  isDragging: boolean = false;

  includeRubricsSubscription: Subscription;
  fileSelectedSubscription: Subscription;
  updateDropdownSubscription: Subscription;

  _signatories: any[] = [];
  pageSelected = 1;
  private previousStep: any;

  renderDropdown: boolean = true;

  ghostElement: HTMLElement | null = null;

  signatoryCustomDropdown: any = {
    items: [],
    displayText: "description",
    placeholder: this.translate.instant('envelope.main-envelope.asideLeft.custom-Fields.selectSignatory'),
    showSearch: true,
    groups: [],
    clearSelectionItem: false,
    groupsRenderingProps: {
      hasIcons: true,
      hasBadges: false,
      hasSubGroups: false
    },
    listMaxHeight: 150,
    listMaxWidth: 322
  };

  constructor(
    private toastService: ToastService,
    public interactService: InteractService,
    private componentDragDropService: ComponentDragDropService,
    public newEnvelopeService: NewEnvelopeService,
    private translate: TranslateService,
    private googleAnalitycsService: AnalyticsService) {

    this.newEnvelopeService.getSubjectPageSelected().subscribe(x => {
      this.pageSelected = parseInt(x);
    });
  }

  ngOnInit(): void {
    this.componentDragDropService.isDragging.subscribe(isDragging => {
      this.isDragging = isDragging;
    });

    this.includeRubricsSubscription = this.newEnvelopeService
      .getSubjectIncludeRubrics().subscribe((x) => {
        this.newEnvelopeService.includeRubrics = x;
      });

    this.fileSelectedSubscription = this.newEnvelopeService.getSubjectfileSelected().subscribe(() => {
      this.updateSignatoryDropdownStatus();
    });

    this.updateDropdownSubscription = this.newEnvelopeService.getSubjectUpdateDropdown().subscribe(() => {
      this.updateSignatoryDropdownStatus();
    });
  }

  ngDoCheck(): void {
    const currentStep = this.newEnvelopeService.getCurrentStep();
    if (this.previousStep !== currentStep) {
      this.previousStep = currentStep;

      if (currentStep === NewEnvelopeSteps.Signatures) {
        this.resetDropdown();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.includeRubricsSubscription) this.includeRubricsSubscription.unsubscribe();
    if (this.fileSelectedSubscription) this.fileSelectedSubscription.unsubscribe();
    if (this.updateDropdownSubscription) this.updateDropdownSubscription.unsubscribe();

    interact('.drag-signature-button').unset();
    interact('.drag-rubric-button').unset();
    interact('.drag-custom-field-button').unset();
    this.removeGhostElement();
  }

  resetDropdown() {
    this.renderDropdown = false;
    this.signatorySelected = null;
    this.classComponent = 'drag-drop disabled';

    this.updateSignatoryDropdownStatus();

    setTimeout(() => { this.renderDropdown = true; }, 10);
  }

  updateSignatoryDropdownStatus() {
    if (!this._signatories || this._signatories.length <= 0) {
      this.signatoryCustomDropdown = {
        displayText: "description",
        placeholder: this.translate.instant('envelope.main-envelope.asideLeft.custom-Fields.selectSignatory'),
        showSearch: true,
        groups: [],
        clearSelectionItem: false,
        groupsRenderingProps: { hasIcons: true, hasBadges: false, hasSubGroups: false },
        listMaxHeight: 150,
        listMaxWidth: 322
      };
      return;
    }

    const currentFile = this.newEnvelopeService.envelopeFiles.find(x => x.checked === true);
    const fileName = currentFile ? currentFile.name : '';

    const groupItems: any[] = this._signatories.map(y => {
        const hasSignature = this.newEnvelopeService.validateSignatureExistsInFile(y.id, fileName);
        return {
            selected: this.signatorySelected ? this.signatorySelected.id === y.id : false,
            class: hasSignature ? 'text-success' : 'text-warning',
            icon: {
                prefix: 'fas',
                iconName: hasSignature ? 'check' : 'exclamation-triangle'
            },
            properties: {
                id: y.id,
                description: y.name
            }
        };
    });

    this.signatoryCustomDropdown = {
      displayText: "description",
      placeholder: this.translate.instant('envelope.main-envelope.asideLeft.custom-Fields.selectSignatory'),
      showSearch: true,
      groups: [groupItems],
      clearSelectionItem: false,
      groupsRenderingProps: {
        hasIcons: true,
        hasBadges: false,
        hasSubGroups: false
      },
      listMaxHeight: 150,
      listMaxWidth: 322
    };
  }

  ngAfterViewInit() {
    if (!this.isMobile) {
      this.initializeDragFromButtons();
    }
  }

  onSelectDropdownItem(event: any) {
    if (!event) return;

    const selected = event.properties ? event.properties : event;

    this.signatorySelected = { id: selected.id, value: selected.description };
    this.classComponent = 'drag-drop';

    this.updateSignatoryDropdownStatus();
  }

  onChangeIncludeSignature(event) {
    this.googleAnalitycsService.sendEvent(
      new TrackerData(
        AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
        'Signer_NovEnv_AssinaturasAutomaticas',
        event ? 'Ativo' : 'Inativo'
      )
    );

    this.newEnvelopeService.includeSignature = event;
    if (this.newEnvelopeService.includeSignature) {
      this.newEnvelopeService.includeSignatureMode();
    } else {
      this.newEnvelopeService.excludeSignatureMode();
    }
    setTimeout(() => this.updateSignatoryDropdownStatus(), 100);
  }

  onChangeIncludeRubric(event) {
    this.googleAnalitycsService.sendEvent(
      new TrackerData(
        AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
        'Signer_NovEnv_RubricasAutomaticas',
        event ? 'Ativo' : 'Inativo'
      )
    );

    this.newEnvelopeService.includeRubrics = event;
    if (this.newEnvelopeService.includeRubrics) {
      this.newEnvelopeService.includeRubricMode();
    } else {
      this.newEnvelopeService.excludeRubricMode();
    }
    setTimeout(() => this.updateSignatoryDropdownStatus(), 100);
  }

  initializeDragFromButtons() {
    interact('.drag-signature-button')
      .draggable({
        manualStart: true,
        onstart: (event) => {
          if (this.signatorySelected === null) return;
          document.body.classList.add('dragging-stamp');
          this.createGhostElement('signature', event);
        },
        onmove: (event) => {
          this.moveGhostElement(event);
        },
        onend: (event) => {
          document.body.classList.remove('dragging-stamp');
          if (this.signatorySelected !== null) {
            this.dropGhostElement(event, 'signature');
          }
        }
      })
      .on('move', (event) => {
        const interaction = event.interaction;
        if (interaction.pointerIsDown && !interaction.interacting() && this.signatorySelected !== null) {
          const target = event.currentTarget;
          interaction.start({ name: 'drag' }, event.interactable, target);
        }
      });

    interact('.drag-rubric-button')
      .draggable({
        manualStart: true,
        onstart: (event) => {
          if (this.signatorySelected === null || !this.interactService.withRubrics) return;
          document.body.classList.add('dragging-stamp');
          this.createGhostElement('rubric', event);
        },
        onmove: (event) => {
          this.moveGhostElement(event);
        },
        onend: (event) => {
          document.body.classList.remove('dragging-stamp');
          if (this.signatorySelected !== null) {
            this.dropGhostElement(event, 'rubric');
          }
        }
      })
      .on('move', (event) => {
        const interaction = event.interaction;
        if (interaction.pointerIsDown && !interaction.interacting() &&
          this.signatorySelected !== null && this.interactService.withRubrics) {
          const target = event.currentTarget;
          interaction.start({ name: 'drag' }, event.interactable, target);
        }
      });

    interact('.drag-custom-field-button')
      .draggable({
        manualStart: true,
        onstart: (event) => {
          if (!this.newEnvelopeService.includeComponent) return;
          const customFieldType = this.getCustomFieldTypeFromElement(event.currentTarget as HTMLElement);
          if (!customFieldType) return;
          document.body.classList.add('dragging-stamp');
          this.createGhostElement(customFieldType, event);
        },
        onmove: (event) => {
          this.moveGhostElement(event);
        },
        onend: (event) => {
          const customFieldType = this.getCustomFieldTypeFromElement(event.currentTarget as HTMLElement);
          document.body.classList.remove('dragging-stamp');
          if (customFieldType) {
            this.dropGhostElement(event, customFieldType);
          }
        }
      })
      .on('move', (event) => {
        const interaction = event.interaction;
        const customFieldType = this.getCustomFieldTypeFromElement(event.currentTarget as HTMLElement);
        if (interaction.pointerIsDown && !interaction.interacting() && customFieldType && this.newEnvelopeService.includeComponent) {
          const target = event.currentTarget;
          interaction.start({ name: 'drag' }, event.interactable, target);
        }
      });
  }

  createGhostElement(type: 'signature' | 'rubric' | CustomFieldTypeEnum, event: any) {
    this.removeGhostElement();
    if (this.isDragging) return;

    const isSignature = type === 'signature';
    const isRubric = type === 'rubric';
    const isCheckbox = type === CustomFieldTypeEnum.CheckboxField;
    const ghostElementHeight = isSignature ? '80px' : (isRubric ? '48px' : (isCheckbox ? '20px' : '25px'));
    const ghostElementWidth = isSignature ? '185px' : (isRubric ? '52px' : (isCheckbox ? '20px' : '250px'));
    const ghostElementFontSize = isSignature ? '14px' : '12px';
    const contentLabel = this.getGhostLabel(type);

    this.ghostElement = document.createElement('div');
    this.ghostElement.className = 'stamp-ghost';
    this.ghostElement.style.height = ghostElementHeight;
    this.ghostElement.style.width = ghostElementWidth;
    this.ghostElement.style.position = 'fixed';
    this.ghostElement.style.pointerEvents = 'none';
    this.ghostElement.style.zIndex = '10000';
    this.ghostElement.style.opacity = '0.7';
    this.ghostElement.style.transform = 'translate(-50%, -50%)';

    const icon = document.createElement('div');

    icon.innerHTML = `
      <div class="primary" style="
        height: ${ghostElementHeight};
        width: ${ghostElementWidth};
        background: white;
        border: 1px dashed;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: ${ghostElementFontSize};
        white-space: nowrap;
      ">
        ${isCheckbox ? '<i class="far fa-check-square"></i>' : contentLabel}
      </div>
    `;

    this.ghostElement.appendChild(icon);
    document.body.appendChild(this.ghostElement);

    const pointerX = event.clientX ?? (event.pageX ?? 0);
    const pointerY = event.clientY ?? (event.pageY ?? 0);
    this.ghostElement.style.left = `${pointerX}px`;
    this.ghostElement.style.top = `${pointerY}px`;
  }

  private getGhostLabel(type: 'signature' | 'rubric' | CustomFieldTypeEnum): string {
    if (type === 'signature') {
      return this.translate.instant('envelope.main-envelope.dragAndDropSignature');
    }

    if (type === 'rubric') {
      return this.translate.instant('envelope.main-envelope.dragAndDropRubric');
    }

    return this.translate.instant(`envelope.main-envelope.asideLeft.custom-Fields.components.${type.toString()}`);
  }

  moveGhostElement(event: any) {
    if (!this.ghostElement) return;

    const x = parseFloat(this.ghostElement.style.left) || 0;
    const y = parseFloat(this.ghostElement.style.top) || 0;

    this.ghostElement.style.left = `${x + event.dx}px`;
    this.ghostElement.style.top = `${y + event.dy}px`;
  }

  dropGhostElement(event: any, type: 'signature' | 'rubric' | CustomFieldTypeEnum) {
    if (!this.ghostElement) return;

    const ghostRect = this.ghostElement.getBoundingClientRect();
    let dropX = ghostRect.left - (type === 'signature' ? 13.45 : (type === 'rubric' ? 10 : 0));
    let dropY = ghostRect.top - (type === 'signature' ? 8 : (type === 'rubric' ? 10 : 0));

    const elementAtPointTopLeft = document.elementFromPoint(ghostRect.left, ghostRect.top) as HTMLElement;
    const elementAtPointTopRight = document.elementFromPoint(ghostRect.right, ghostRect.top) as HTMLElement;
    const elementAtPointBottomLeft = document.elementFromPoint(ghostRect.left, ghostRect.bottom) as HTMLElement;
    const elementAtPointBottomRight = document.elementFromPoint(ghostRect.right, ghostRect.bottom) as HTMLElement;

    const pageTopLeft = elementAtPointTopLeft?.closest('.page.dropzone') as HTMLElement;
    const pageTopRight = elementAtPointTopRight?.closest('.page.dropzone') as HTMLElement;
    const pageBottomLeft = elementAtPointBottomLeft?.closest('.page.dropzone') as HTMLElement;
    const pageBottomRight = elementAtPointBottomRight?.closest('.page.dropzone') as HTMLElement;

    let droppedOnPage = false;

    if (pageTopLeft && pageTopRight && pageBottomLeft && pageBottomRight) {
      droppedOnPage = true;

      const pageRect = pageTopLeft.getBoundingClientRect();
      const relativeX = dropX - pageRect.left;
      const relativeY = dropY - pageRect.top;

      const pageNumber = pageTopLeft.dataset.pageNumber;
      this.pageSelected = parseInt(pageNumber);
      this.newEnvelopeService.setSubjectPageSelected(this.pageSelected.toString());

        if (type === 'signature') {
          this.createElementSignatureAtPosition(relativeX, relativeY);
        } else if (type === 'rubric') {
          this.createElementRubricAtPosition(relativeX, relativeY);
        } else {
          this.createElementCustomFieldAtPosition(relativeX, relativeY, type);
        }
    }

    var isStamps = type === 'signature' || type === 'rubric';
    if (droppedOnPage) {
      const label = this.resolveAnalyticsEvent(isStamps, type);
      this.sendAnalyticsEvent(label);
    } else {
      var toastMessage = isStamps ? 'envelope.main-envelope.dragAndDropNotOnDocumentToast' : 'envelope.main-envelope.dragAndDropNotOnPageToast';
      const toast: IToast = {
        text: this.translate.instant(toastMessage),
        autoHide: true,
        type: 'warning'
      };
      this.toastService.createNewToast(toast);
    }

    this.removeGhostElement();
  }

  removeGhostElement() {
    if (this.ghostElement && this.ghostElement.parentNode) {
      this.ghostElement.parentNode.removeChild(this.ghostElement);
      this.ghostElement = null;
    }
  }

  createElementSignatureAtPosition(x: number, y: number) {
    if (this.signatorySelected === null) return;
    let envelopeFileSelected = this.newEnvelopeService.envelopeFiles.find(x => x.checked == true);
    let signatorys = this.newEnvelopeService.getModelData(NewEnvelopeSteps.Signatory)["elements"] as Array<Signatory>
    let signatory = signatorys.find(x => x.id.toString() == this.signatorySelected.id);

    let exits = this.newEnvelopeService.validateSignatureExistsInFile(signatory.id, envelopeFileSelected.name);

    if (signatorys.find(x => x.signatureType.id == 0 || x.signatureType.id == 1)) {
      if (exits && !this.interactService.withRubrics) {
        const toast: IToast = { text: this.translate.instant('businessError.rubricWarning'), autoHide: true, type: 'warning' };
        this.toastService.createNewToast(toast);
        return;
      }
    };

    if (exits && this.newEnvelopeService.envelopeFiles.some(x => x.upload == true && x.fileAlreadySigned)) {
      this.toastFilePreviouslySigned('signature');
        return;
    }

    this.newEnvelopeService.setSubjectPageSelected(this.pageSelected.toString());
    this.componentDragDropService.createElementSignatureAddStamp(envelopeFileSelected.name, signatory, this.pageSelected, x, y);

    setTimeout(() => this.updateSignatoryDropdownStatus(), 100);
  }

  createElementRubricAtPosition(x: number, y: number) {
    if (this.signatorySelected === null) return;
    if (!this.interactService.withRubrics) return;

    if (this.newEnvelopeService.envelopeFiles.some(x => x.upload == true && x.fileAlreadySigned)) {
      this.toastFilePreviouslySigned('rubric');
      return;
    }

    let envelopeFileSelected = this.newEnvelopeService.envelopeFiles.find(x => x.checked == true);
    let signatorys = this.newEnvelopeService.getModelData(NewEnvelopeSteps.Signatory)["elements"] as Array<Signatory>

    let signatory = signatorys.find(x => x.id.toString() == this.signatorySelected.id)
    this.newEnvelopeService.setSubjectPageSelected(this.pageSelected.toString());
    this.componentDragDropService.createElementRubricAddStamp(envelopeFileSelected.name, signatory, this.pageSelected, x, y);

    setTimeout(() => this.updateSignatoryDropdownStatus(), 100);
  }

  createElementCustomFieldAtPosition(x: number, y: number, customFieldType: CustomFieldTypeEnum) {
    if (!this.newEnvelopeService.includeComponent) return;

    const envelopeFileSelected = this.newEnvelopeService.envelopeFiles.find(x => x.checked == true);
    const responsible = this.getResponsibleForCustomField();
    if (!envelopeFileSelected || !responsible) return;

    this.newEnvelopeService.setSubjectPageSelected(this.pageSelected.toString());
    this.componentDragDropService.createElementCustomFieldAdd(
      envelopeFileSelected.name,
      responsible,
      this.pageSelected,
      x,
      y,
      customFieldType
    );
  }

  private getResponsibleForCustomField(): any | undefined {
    const responsibles = this.newEnvelopeService.getModelData(NewEnvelopeSteps.Signatory)["envelopeResponsible"];
    if (!responsibles || responsibles.length === 0) {
      return undefined;
    }

    return responsibles[0];
  }

  private getCustomFieldTypeFromElement(element: HTMLElement): CustomFieldTypeEnum | null {
    if (!element) {
      return null;
    }

    const type = element.getAttribute('data-custom-field-type');
    if (type === CustomFieldTypeEnum.TextField ||
      type === CustomFieldTypeEnum.CheckboxField) {
      return type as CustomFieldTypeEnum;
    }

    return null;
  }
  handleToggleClickRubric(event: Event) {
    if (this.newEnvelopeService.envelopeFiles.some(x => x.upload == true && x.fileAlreadySigned)) {
      event.preventDefault();
      event.stopPropagation();
      this.toastFilePreviouslySigned('rubric');
    }
  }

  toastFilePreviouslySigned(type: 'rubric' | 'signature') {
    const toast: IToast = {
      text: this.translate.instant(type === 'signature' ?
         'businessError.twoOrMoreStampsPreviouslySignedDoc' :
         'businessError.rubricPreviouslySignedDoc'),
      autoHide: true,
      type: 'warning'
    };
    this.toastService.createNewToast(toast);
  }

  restoreDocument() {
    this.newEnvelopeService.excludeAllSignature();
    this.newEnvelopeService.includeSignature = false;
    this.newEnvelopeService.excludeAllRubric();
    this.newEnvelopeService.includeRubrics = false;
    this.newEnvelopeService.excludeAllCustomFields();
    this.updateSignatoryDropdownStatus();
  }

  private resolveAnalyticsEvent(isStamps: boolean, type: 'signature' | 'rubric' | CustomFieldTypeEnum) {
    if (isStamps)
      return type === 'signature' ?
        'Signer_NovEnv_AssinaturasManuais' :
        'Signer_NovEnv_RubricasManuais'

    return type === CustomFieldTypeEnum.TextField ?
        'Signer_NovEnv_FormTexto' :
        'Signer_NovEnv_FormCaixaSelecao'
  }

  private sendAnalyticsEvent(label: string) {
    this.googleAnalitycsService.sendEvent(
      new TrackerData(
        AnalyticEventClient.TK_PT_SIGNER_NEW_ENVELOPE,
        label
      )
    );
  }
}
